import { models as startModels,stores } from '@kurtosys/app-start';
import { EventTypes } from '@kurtosys/event-manager/dist/models/EventTypes.js';
import { components } from '@kurtosys/ksys-app-components';
import { models } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
/* [Component: appStoreComponentImport] */
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration.js';
import { MockData } from '../../../configuration/mockData/index.js';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { IFactList } from '../../../models/app/IFactList.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { Feature } from '../../shared/Feature.js';
import { IAppComponents } from '../models/IAppComponents.js';

type IAppDevelopmentConfig = startModels.IAppDevelopmentConfig;
type ICardProps = components.base.Card.models.ICardProps;
type IGridProps = components.base.Grid.models.IGridProps;
type IFactProps = components.fact.Fact.models.IFactProps;
type IFactListProps = components.fact.FactList.models.IFactListProps;
type IGrid = components.base.Grid.models.IGrid;

let devConfig: IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	static EVENT_BUS_KEY: EventTypes = EventTypes.GLOBAL_INPUTS_CHANGE;
	url: URL;

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
		mockData?: MockData,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig, mockData);
		this.url = new URL(url);
		makeObservable(this);
	}

	@computed
	get hasData(): boolean {
		let hasData = false;
		const factLists = this.activeFactListProps;
		const factListKeys = Object.keys(factLists);
		if (factListKeys.length > 0) {
			for (let i = 0; i < factListKeys.length; i++) {
				const factListKey = factListKeys[i];
				const listProps = factLists[factListKey];
				if (listProps && listProps.listBlankDataOptions) {
					const { showBlank } = listProps.listBlankDataOptions;
					if (showBlank === false) {
						const { items } = listProps;
						hasData = items && items.some((item) => this.listItemHasData(item));
					} else {
						hasData = true;
					}
				} else {
					hasData = true;
				}
				if (hasData) {
					break;
				}
			}
		}
		return hasData;
	}

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	};

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get cardProps(): ICardProps | undefined {
		const cardProps = this.appComponentConfiguration && this.appComponentConfiguration.cardProps;
		return this.mergeQueriesAndProps(cardProps);
	}

	@computed
	get gridProps(): IGridProps | undefined {
		const gridProps = this.appComponentConfiguration && this.appComponentConfiguration.gridProps;
		if (gridProps && gridProps.grid) {
			this.setGridContent(gridProps.grid);
		}
		if (gridProps && gridProps.breakpoints) {
			gridProps.breakpoints.forEach((breakpoint) => {
				if (breakpoint.props && breakpoint.props.grid) {
					this.setGridContent(breakpoint.props.grid);
				}
			});
		}
		return gridProps;
	}

	listItemHasData(item: IFactProps): boolean {
		let value;
		if (item.riskRatingProps) {
			value = item.riskRatingProps.value;
		} else if (item.morningstarRatingProps) {
			value = item.morningstarRatingProps.morningStarRating;
		} else {
			value = item.value;
		}
		return !common.commonUtils.isNullOrEmpty(value);
	}

	@action
	setGridContent(grid: IGrid | undefined) {
		if (common.commonUtils.isNullOrUndefined(grid)) {
			return grid;
		}
		for (const row of grid.rows) {
			for (const column of row.columns) {
				for (const component of column.components) {
					const { key = '', props } = component;
					const listKey = (props && props.listKey) || key;
					const listProps = common.commonUtils.deepMergeObjects(
						props,
						this.getFactListProps(listKey),
					) as IFactListProps;
					let componentListProps;
					if (listProps && listProps.listBlankDataOptions) {
						const { showBlank } = listProps.listBlankDataOptions;
						if (showBlank === false) {
							const { items } = listProps;
							const listHasData = items && items.some((item) => this.listItemHasData(item));
							componentListProps = listHasData
								? listProps
								: { listBlankDataOptions: listProps.listBlankDataOptions };
						} else {
							componentListProps = listProps;
						}
					} else {
						componentListProps = listProps;
					}
					component.props = componentListProps;
				}
			}
		}
	}

	@computed
	get activeFactListProps(): { [key: string]: IFactListProps } {
		const activeFundLists: { [key: string]: IFactListProps } = {};

		const retrieveFundListsFromGrid = (grid: IGrid) => {
			for (const row of grid.rows) {
				for (const column of row.columns) {
					for (const component of column.components) {
						const { key = '', props } = component;
						const listKey: string = (props && props.listKey) || key;
						if (!activeFundLists[listKey]) {
							activeFundLists[listKey] = common.commonUtils.deepMergeObjects(
								props,
								this.getFactListProps(listKey),
							) as IFactListProps;
						}
					}
				}
			}
		};

		if (this.appComponentConfiguration) {
			const { gridProps } = this.appComponentConfiguration;
			if (gridProps) {
				const { grid, breakpoints } = gridProps;
				if (grid) {
					retrieveFundListsFromGrid(grid);
				} else if (breakpoints) {
					breakpoints.forEach((breakpoint) => {
						const { props } = breakpoint;
						if (props) {
							const { grid } = props;
							if (grid) {
								retrieveFundListsFromGrid(grid);
							}
						}
					});
				}
			}
		}

		return activeFundLists;
	}

	getFactListProps(listKey: string): IFactListProps {
		const { items = [], ...list } = this.getList(listKey);
		const factListResult = this.mergeQueriesAndProps(list) as IFactListProps;
		const factPropItems = items.map((item) => this.mergeQueriesAndProps(item) as IFactProps);
		const { itemBlankDataOptions, listBlankDataOptions, listValueSeparator } = factListResult;
		return {
			...factListResult,
			items: factPropItems,
			itemBlankDataOptions: { ...this.itemBlankDataOptions, ...itemBlankDataOptions },
			listBlankDataOptions: { ...this.listBlankDataOptions, ...listBlankDataOptions },
			listValueSeparator: listValueSeparator || this.listValueSeparator,
		};
	}

	getList(listKey: string): IFactList {
		const listConfig = this.listConfiguration.filter((config) => config.key === listKey);
		if (listConfig.length > 0) {
			return listConfig[0];
		}

		const defaultList: IFactList = {
			key: listKey,
			props: {},
			queries: {},
			items: [],
		};

		return defaultList;
	}

	@computed
	get listConfiguration(): IFactList[] {
		if (this.configuration && this.configuration.components && this.configuration.components.factLists) {
			return this.configuration.components.factLists;
		}
		return [];
	}
	@computed
	get listValueSeparator(): string {
		if (this.configuration && this.configuration.components && this.configuration.components.listValueSeparator) {
			return this.configuration.components.listValueSeparator;
		}
		return ',';
	}
	@computed
	get listBlankDataOptions(): models.IBlankDataOptions {
		if (this.configuration && this.configuration.components && this.configuration.components.listBlankDataOptions) {
			return this.configuration.components.listBlankDataOptions;
		}
		return {};
	}
	@computed
	get itemBlankDataOptions(): models.IBlankDataOptions {
		if (this.configuration && this.configuration.components && this.configuration.components.itemBlankDataOptions) {
			return this.configuration.components.itemBlankDataOptions;
		}
		return {};
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	async customInitializeAfter() {
		// TODO: Initialize stores here. See https://github.com/Kurtosys/studio-apps/blob/7c5456503e664db90e39a1f8fd8f9edd38f892b6/apps/ksys-app-global-inputs/src/components/App/stores/AppStore.ts#L197
	}
}
